/**
 * Last-known company brand (name + logo), mirrored to localStorage.
 *
 * The app chrome renders before `GET /api/company-profile` resolves, so without
 * a cache the sidebar painted the neutral product default ("Firmio") and then
 * swapped to the real company name a moment later. Persisting just the two
 * fields the chrome needs makes the first paint correct on every visit after
 * the first; React Query still refetches in the background, so a rename or a
 * new logo shows up on the next load.
 *
 * Deliberately stores ONLY the brand name and logo — never the rest of the
 * profile (IBAN, contact email…), which has no business sitting in
 * localStorage. Cleared on logout so the next account never inherits it.
 */

const KEY = 'firmio.brand';

export interface CachedBrand {
  name: string;
  /** Data URL of the square logo, or null when the company has none. */
  logoUrl: string | null;
}

export function readBrandCache(): CachedBrand | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedBrand> | null;
    if (!parsed || typeof parsed.name !== 'string' || !parsed.name) return null;
    return {
      name: parsed.name,
      logoUrl: typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null,
    };
  } catch {
    // Unparseable or unavailable storage: fall back to the loading state.
    return null;
  }
}

export function writeBrandCache(brand: CachedBrand): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(brand));
  } catch {
    // Quota exceeded / private mode: the cache is an optimisation, never a
    // requirement, so a failed write must not break rendering.
  }
}

export function clearBrandCache(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // See writeBrandCache.
  }
}
